import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useGetWipLineItemsQuery,
  WipLineItem,
  WipLineItemsResult,
} from "../../../store/apis/wipApi";
import {
  fmtInt,
  fmtMoney,
  fmtMoneyDetailed,
  fmtPct,
} from "../../../utils/format";

/** Unidad del volumen según el objetivo de la línea. */
const VOLUME_LABEL: Record<string, string> = {
  CPM: "Impresiones",
  CPC: "Clicks",
  CPV: "Visualizaciones",
  CPMV: "Visualizaciones",
  CPA: "Conversiones",
  CPL: "Conversiones",
  CPE: "Impresiones",
};

const BUDGET_LABEL: Record<string, string> = {
  AUTOMATIC: "Automático",
  FIXED: "Fijo",
  UNLIMITED: "Ilimitado",
  UNKNOWN: "—",
};

const BUDGET_HINT: Record<string, string> = {
  AUTOMATIC: "Lo reparte la IO: al subir o bajar la IO, el line item la sigue.",
  FIXED:
    "Importe propio: no gasta más de esto aunque la IO tenga más presupuesto.",
  UNLIMITED: "Sin tope propio: gasta hasta lo que tenga la IO.",
  UNKNOWN: "DV360 no ha devuelto el tipo de presupuesto.",
};

const STATUS_COLOR: Record<
  string,
  "success" | "warning" | "default" | "error"
> = {
  ACTIVE: "success",
  PAUSED: "warning",
  DRAFT: "default",
  ARCHIVED: "default",
};

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: "Activo",
  PAUSED: "Pausado",
  DRAFT: "Borrador",
  ARCHIVED: "Archivado",
};

const fmtDay = (iso: string | null): string =>
  iso ? iso.split("-").reverse().join("/") : "—";

const BudgetCell: React.FC<{ li: WipLineItem }> = ({ li }) => {
  if (!li.budget) return <>—</>;
  const { type, maxAmount, inImpressions } = li.budget;
  const amount =
    maxAmount == null
      ? null
      : inImpressions
        ? `${fmtInt(maxAmount)} impr.`
        : fmtMoney(maxAmount, 2);
  return (
    <Tooltip title={BUDGET_HINT[type] ?? ""}>
      <span>
        <strong>{BUDGET_LABEL[type] ?? type}</strong>
        {amount ? ` · ${amount}` : ""}
      </span>
    </Tooltip>
  );
};

const headSx = { fontWeight: 700, whiteSpace: "nowrap" } as const;
const numSx = { whiteSpace: "nowrap" } as const;

/** Tabla de line items de una IO: gasto, volumen y presupuesto de cada uno. */
const LineItemsTable: React.FC<{
  data: WipLineItemsResult;
  rows: WipLineItem[];
}> = ({ data, rows }) => {
  const volumeLabel = VOLUME_LABEL[data.objective] ?? "Volumen";
  const isCpm = data.objective === "CPM" || data.objective === "CPMV";
  return (
    <Table size="small" sx={{ "& td, & th": { fontSize: 12, py: 0.5 } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={headSx}>Line item</TableCell>
          <TableCell sx={headSx}>Estado</TableCell>
          <TableCell sx={headSx}>Vuelo</TableCell>
          <TableCell sx={headSx}>Presupuesto DV360</TableCell>
          <TableCell sx={headSx} align="right">
            Gasto hasta ayer
          </TableCell>
          <TableCell sx={headSx} align="right">
            <Tooltip title="Presupuesto fijo menos lo gastado hasta ayer. Solo en line items con presupuesto fijo.">
              <span>Le queda</span>
            </Tooltip>
          </TableCell>
          <TableCell sx={headSx} align="right">
            <Tooltip title="Parte del gasto de los line items de estas fechas que se lleva cada uno.">
              <span>% gasto</span>
            </Tooltip>
          </TableCell>
          <TableCell sx={headSx} align="right">
            {volumeLabel} hasta ayer
          </TableCell>
          <TableCell sx={headSx} align="right">
            {isCpm ? "CPM real" : "Coste unit. real"}
          </TableCell>
          <TableCell sx={headSx} align="right">
            {volumeLabel} ayer
          </TableCell>
          <TableCell sx={headSx} align="right">
            Gasto ayer
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((li) => (
          <TableRow
            key={li.lineItemId}
            hover
            sx={li.inPeriod ? undefined : { opacity: 0.55 }}
          >
            <TableCell sx={{ minWidth: 220, maxWidth: 380 }}>
              <Typography
                sx={{ fontSize: 12, fontWeight: 600, overflowWrap: "anywhere" }}
              >
                {li.name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                {li.lineItemId}
                {li.inPeriod ? "" : " · otro vuelo"}
              </Typography>
            </TableCell>
            <TableCell>
              {li.status ? (
                <Chip
                  size="small"
                  label={STATUS_LABEL[li.status] ?? li.status}
                  color={STATUS_COLOR[li.status] ?? "default"}
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11 }}
                />
              ) : (
                "—"
              )}
            </TableCell>
            <TableCell sx={numSx}>
              {li.flightStartDate || li.flightEndDate
                ? `${fmtDay(li.flightStartDate)} – ${fmtDay(li.flightEndDate)}`
                : "Como la IO"}
            </TableCell>
            <TableCell sx={numSx}>
              <BudgetCell li={li} />
            </TableCell>
            <TableCell align="right" sx={numSx}>
              {fmtMoney(li.platformCost, 2)}
            </TableCell>
            <TableCell
              align="right"
              sx={{
                ...numSx,
                color:
                  li.remainingBudget != null && li.remainingBudget <= 0
                    ? "error.main"
                    : undefined,
                fontWeight: li.remainingBudget != null ? 600 : undefined,
              }}
            >
              {fmtMoney(li.remainingBudget, 2)}
            </TableCell>
            <TableCell align="right" sx={numSx}>
              {li.inPeriod ? fmtPct(li.spendShare * 100) : "—"}
            </TableCell>
            <TableCell align="right" sx={numSx}>
              {fmtInt(li.volume)}
            </TableCell>
            <TableCell align="right" sx={numSx}>
              {li.realUnitCostPlatform == null
                ? "—"
                : fmtMoneyDetailed(li.realUnitCostPlatform)}
            </TableCell>
            <TableCell align="right" sx={numSx}>
              {fmtInt(li.yesterdayVolume)}
            </TableCell>
            <TableCell align="right" sx={numSx}>
              {fmtMoney(li.yesterdayPlatformCost, 2)}
            </TableCell>
          </TableRow>
        ))}
        <TableRow sx={{ "& td": { fontWeight: 700 } }}>
          <TableCell colSpan={4}>Total de estas fechas</TableCell>
          <TableCell align="right" sx={numSx}>
            {fmtMoney(data.totals.platformCost, 2)}
          </TableCell>
          <TableCell align="right" sx={numSx}>
            {fmtMoney(data.fixedRemainingBudget, 2)}
          </TableCell>
          <TableCell />
          <TableCell align="right" sx={numSx}>
            {fmtInt(data.totals.volume)}
          </TableCell>
          <TableCell />
          <TableCell align="right" sx={numSx}>
            {fmtInt(data.totals.yesterdayVolume)}
          </TableCell>
          <TableCell align="right" sx={numSx}>
            {fmtMoney(data.totals.yesterdayPlatformCost, 2)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

interface LineItemsPanelProps {
  associatedCampaignId: string;
  /** Ancho visible de la tabla: el panel no se desplaza con el scroll horizontal. */
  width: number;
}

/** Desplegable de una línea de DV360 a nivel IO con sus line items. Solo lectura. */
const LineItemsPanel: React.FC<LineItemsPanelProps> = ({
  associatedCampaignId,
  width,
}) => {
  const { data, isLoading, isError } =
    useGetWipLineItemsQuery(associatedCampaignId);
  const result = data?.data ?? null;
  // Los line items de otros vuelos de la misma IO, ocultos salvo que se pidan.
  const [showOther, setShowOther] = useState(false);
  const current = result?.lineItems.filter((li) => li.inPeriod) ?? [];
  const otherCount = (result?.lineItems.length ?? 0) - current.length;
  const period =
    result?.periodStartDate && result.periodEndDate
      ? `del ${fmtDay(result.periodStartDate)} al ${fmtDay(result.periodEndDate)}`
      : null;

  return (
    <Box
      sx={{
        position: "sticky",
        left: 0,
        width: width > 0 ? width - 16 : "100%",
        boxSizing: "border-box",
        py: 1.5,
        px: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Leyendo line items de DV360…</Typography>
        </Box>
      ) : isError || !result ? (
        <Alert severity="error">No se han podido cargar los line items.</Alert>
      ) : (
        <>
          {!result.dv360Available && (
            <Alert severity="warning">
              No se ha podido leer DV360
              {result.dv360Error ? ` (${result.dv360Error})` : ""}: se ven las
              métricas, pero no el presupuesto ni el estado de cada line item.
            </Alert>
          )}
          {result.hasFixedBudgets && (
            <Alert severity="info">
              Hay line items con <strong>presupuesto fijo</strong>: si sube la
              inversión de la IO, no gastarán más de su presupuesto. Les quedan{" "}
              <strong>{fmtMoney(result.fixedRemainingBudget, 2)}</strong> en
              total. Al aceptar un margen que sube la IO se propone subir
              también a los activos, en proporción a su presupuesto. Bajar la
              inversión de la IO funciona siempre.
            </Alert>
          )}
          {result.lineItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Esta IO no tiene line items.
            </Typography>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {current.length === 0
                    ? `Ningún line item tiene vuelo ${period ?? "en estas fechas"}.`
                    : `${current.length} line item${current.length === 1 ? "" : "s"} con vuelo ${period ?? "en estas fechas"}.`}
                </Typography>
                {otherCount > 0 && (
                  <Button
                    size="small"
                    onClick={() => setShowOther((v) => !v)}
                    sx={{ fontSize: 12, textTransform: "none", py: 0 }}
                  >
                    {showOther
                      ? "Ocultar otros vuelos"
                      : `Ver también ${otherCount} de otros vuelos`}
                  </Button>
                )}
              </Box>
              {(current.length > 0 || showOther) && (
                // Scroll propio: el panel se queda fijo al ancho visible del WIP.
                <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                  <LineItemsTable
                    data={result}
                    rows={showOther ? result.lineItems : current}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default LineItemsPanel;
